import styled, {css, keyframes} from "styled-components";
import {eyebrowTextCss} from "theme/typography";
import {brand, fontFamilies, fontWeights, radii} from "theme/tokens";

/**
 * Externalized styles for the Support/Feedback dialog (ui#149, owner externalized-styles rule
 * 2026-07-12). styled-components was chosen over a CSS Module because the surface is theme-aware:
 * every color reads `theme.tokens.*`, so light + dark parity falls out of the token layer for free
 * (same approach as `components/ModalSheet.tsx`, the head anatomy this dialog echoes). A CSS Module
 * would need each token re-bridged to a custom property and the mode re-derived by hand.
 *
 * The component file stays markup + behaviour only; no colors are hardcoded here — tokens only.
 */

/** Positioning context inside the MUI paper so the ✕ can anchor to the top-right of the sheet. */
export const Sheet = styled.div`
	position: relative;
`;

/** The editorial header block (mono eyebrow + serif title + lede), on a card surface. */
export const Head = styled.div`
	position: relative;
	text-align: center;
	padding: 34px 40px 22px;
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};

	@media (max-width: 600px) {
		padding: 28px 24px 18px;
	}
`;

export const HeadInner = styled.div`
	max-width: 420px;
	margin: 0 auto;
`;

/** Top-right ✕ (design `.x-btn`, mirrored from ModalSheet so the two dialogs feel related). */
export const CloseButton = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	/* Above the later positioned <Head> sibling — without this the transparent Head hit-tests over
	   the ✕ and swallows its clicks (ui#158); mirrors ModalSheet's x-btn. */
	z-index: 3;
	width: 40px;
	height: 40px;
	border-radius: 11px;
	border: none;
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	font-size: 16px;
	line-height: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: background 0.14s ease, color 0.14s ease;

	&:hover {
		background: ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Mono uppercase eyebrow (design `.pm-ey`; reads `segInk` in dark, matching the other sheets). */
export const Eyebrow = styled.div`
	${eyebrowTextCss};
	font-weight: 700;
	letter-spacing: 0.1em;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
`;

/** Instrument-Serif display title, sized down for the compact dialog. */
export const Title = styled.h2`
	margin: 9px 0 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(30px, 6vw, 40px);
	line-height: 1.04;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

/** Italic accent word inside the title (design `.pm-h .it`). */
export const TitleAccent = styled.span`
	font-style: italic;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
`;

/** Supportive lede under the title (design `.pm-sub`). */
export const Lede = styled.p`
	margin: 11px auto 0;
	max-width: 380px;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The scrollable content region below the head. */
export const Body = styled.div`
	padding: 22px 30px 26px;

	@media (max-width: 600px) {
		padding: 20px 20px 22px;
	}
`;

/**
 * A REAL form element (ui#326), not a styled div: it is what makes Enter-to-send work and what gives
 * the primary action a `type="submit"` to be the default button of.
 */
export const Form = styled.form`
	/* A plain block wrapper: the fields, the transport error and the actions keep the exact stacking
	   they had as direct children of the Body. display:contents would do the same but historically
	   drops the element from the accessibility tree, which is not a trade worth making for zero gain. */
	margin: 0;
`;

/** Vertical stack of labeled fields. */
export const Fields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 18px;
`;

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/** Small helper line under a field (e.g. the "optional" reply-to hint). */
export const FieldHint = styled.span`
	padding-inline-start: 2px;
	font-size: 12.5px;
	line-height: 1.4;
	/* ink2, not ink3: ink3 fails WCAG AA for this readable hint (2.62:1 light / 3.71:1 dark) — ink3 is
	   the disabled/decorative tier, ink2 is the accessible secondary text tier (ui#302). */
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Live length counter under the message field, mirroring the reminder composer's. Turns `errorInk`
 * at the limit, which is the point the sender needs to notice — input stops being accepted there.
 *
 * `ink2`, not the reminder counter's `ink3`: ink3 is the disabled/decorative tier and fails WCAG AA
 * as readable text (2.62:1 light / 3.71:1 dark), the same reason `FieldHint` above reads ink2 (ui#302).
 */
export const MessageCounter = styled.span<{$atLimit: boolean}>`
	align-self: flex-end;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	color: ${({theme, $atLimit}) => ($atLimit ? theme.tokens.errorInk : theme.tokens.ink2)};
`;

/**
 * Per-field validation line, sitting under the field it belongs to (ui#326). Distinct from
 * {@link ErrorText}: this is something the sender can fix, so it is anchored to the input that is
 * wrong rather than floated above the actions as a verdict on the whole form.
 */
export const FieldError = styled.p`
	margin: 0;
	padding-inline-start: 2px;
	font-size: 12.5px;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/** Inline error line kept above the actions when a send fails (the typed note is preserved). */
export const ErrorText = styled.p`
	margin: 0;
	font-size: 13.5px;
	line-height: 1.45;
	color: ${brand.error};
`;

/** Footer actions: ghost Cancel + primary Send. Full-width stack on small screens. */
export const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 22px;

	@media (max-width: 600px) {
		flex-direction: column-reverse;
		align-items: stretch;
	}
`;

const buttonBase = css`
	height: 46px;
	min-width: 120px;
	padding: 0 20px;
	border-radius: ${radii.control};
	font-family: inherit;
	font-weight: 700;
	font-size: 15px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 9px;
	transition: background 0.16s ease, filter 0.16s ease, color 0.16s ease, opacity 0.16s ease;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	@media (max-width: 600px) {
		width: 100%;
	}
`;

/** Subtle secondary action. */
export const GhostButton = styled.button`
	${buttonBase};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: transparent;
	color: ${({theme}) => theme.tokens.ink2};

	&:hover {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink};
	}
`;

/** Primary CTA in the landing button language (filled brand blue). */
export const SubmitButton = styled.button`
	${buttonBase};
	border: none;
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};

	&:hover:not(:disabled):not([aria-disabled="true"]) {
		background: ${({theme}) => theme.tokens.actionFillHover};
	}

	/* A send in flight marks the button aria-disabled rather than disabled, so it keeps keyboard focus
	   (kitecorp/transferify-ui#3079). It looks exactly as unavailable as a disabled one. */
	&:disabled,
	&[aria-disabled="true"] {
		cursor: default;
		opacity: 0.55;
	}
`;

const spin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

/**
 * In-button spinner shown while a send is in flight. It sits on the filled blue CTA, so its arc is
 * white and its track a translucent white — the same translucent-white-on-dark treatment the design
 * encodes for surfaces layered on a colored fill (see `brand.premInk*`).
 */
export const Spinner = styled.span`
	width: 16px;
	height: 16px;
	border-radius: 50%;
	border: 2px solid ${brand.premInk3};
	border-top-color: ${brand.white};
	animation: ${spin} 0.7s linear infinite;
`;

/** The calm designed success moment that replaces the form after a message is sent. */
export const Success = styled.div`
	text-align: center;
	padding: 8px 6px 6px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`;

/** Teal check disc above the success title. */
export const SuccessMark = styled.div`
	width: 56px;
	height: 56px;
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 26px;
	color: ${({theme}) => theme.tokens.teal};
	background: ${({theme}) => theme.tokens.signalTint};
`;

export const SuccessTitle = styled.h3`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 5.5vw, 34px);
	line-height: 1.05;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SuccessNote = styled.p`
	margin: 0;
	max-width: 340px;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Default chat-bubble trigger button (when no custom `trigger` is supplied). */
export const TriggerButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: ${brand.white};
	cursor: pointer;
	transition: background 0.14s ease;

	&:hover {
		background: ${brand.premInk3};
	}

	&:focus-visible {
		outline: 2px solid ${brand.white};
		outline-offset: 2px;
	}
`;
